"use client"

import * as React from "react"
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { ChartDataTable, ChartFigure, ChartCard } from "@/components/charts-core"
import {
  ChartContainer,
  ChartTooltip,
  chartTooltipKeyboardSyncProps,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { CHART_AXIS_TICK } from "@/lib/chart-typography"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import {
  QUALITY_TREND_SERIES,
  type ExperienceTrendMetric,
} from "@/lib/mock/evaluation-quality-dashboard"

const TREND_CFG: ChartConfig = {
  value: { label: "Score", color: "var(--color-chart-2)" },
}

const METRICS: ExperienceTrendMetric[] = [
  "overall",
  "course",
  "faculty",
  "clinical",
  "preceptor",
]

export function QualityExperienceTrend() {
  const [metric, setMetric] = React.useState<ExperienceTrendMetric>("overall")
  const series = QUALITY_TREND_SERIES[metric]
  const data = series.points.map(point => ({ name: point.term, value: point.value }))

  return (
    <section aria-labelledby="quality-trend-heading" className="flex flex-col gap-4">
      <DashboardSectionTitle as="h2" id="quality-trend-heading">
        Experience trend
      </DashboardSectionTitle>

      <Tabs
        value={metric}
        onValueChange={value => setMetric(value as ExperienceTrendMetric)}
      >
        <TabsList variant="line" className="w-full justify-start overflow-x-auto">
          {METRICS.map(key => (
            <TabsTrigger key={key} value={key}>
              {QUALITY_TREND_SERIES[key].label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <ChartCard title={`${series.label} experience`} description="Current vs prior terms">
        <ChartFigure
          label={`${series.label} experience trend`}
          summary={data.map(row => `${row.name} ${row.value.toFixed(1)}`).join(", ")}
          dataLength={data.length}
        >
          {activeIndex => (
            <>
              <ChartContainer config={TREND_CFG} className="h-[240px] w-full">
                <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={CHART_AXIS_TICK} />
                  <YAxis domain={[3.5, 5]} tick={CHART_AXIS_TICK} width={32} />
                  <ChartTooltip
                    key={chartTooltipKeyboardSyncProps(activeIndex).key}
                    {...chartTooltipKeyboardSyncProps(activeIndex).props}
                    content={<ChartTooltipContent />}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-chart-2)"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "var(--color-chart-2)" }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ChartContainer>
              <ChartDataTable
                caption={`${series.label} experience trend`}
                headers={["Term", "Score"]}
                rows={data.map(row => [row.name, row.value.toFixed(1)])}
              />
            </>
          )}
        </ChartFigure>
      </ChartCard>
    </section>
  )
}
